import { useState } from 'react';
import {
    AlertBox,
    AlertBoxDismissButton,
    Button,
    Card,
    Column,
    FlexBox,
    GridContainer,
    Icon,
    Row,
    SkeletonText,
    SkeletonTextHeading,
    Typography,
} from '@vp/swan';
import { useUserContext } from '@vp/ubik-context';
import BusinessNameModal from '../../components/BusinessNameModal/BusinessNameModal';
import { useTrack } from '../../utils/analytics';
import { removePathQueryParams } from '../../utils/removePathQueryParams';
import type { BusinessName } from '../../pages/BusinessNameGeneratorPage/components/BusinessNameResults/BusinessNameResults';

interface BusinessNamesGridProp {
    businessNames: BusinessName[];
    isLoading: boolean;
    trackingEventDetails: {
        category: string;
        pageSection: string;
    };
    keyword?: string;
    industryKeywords?: string[];
}

const getSkeletonBusinessNames = () => {
    const businessNames: BusinessName[] = [];
    for (let i = 0; i < 12; i++) {
        businessNames.push({
            name: `Business Name ${i}`,
            description: `Business Name Description ${1}`,
        });
    }
    return businessNames;
};

const BusinessNamesGrid = ({
    businessNames,
    isLoading,
    trackingEventDetails,
    keyword,
    industryKeywords,
}: BusinessNamesGridProp) => {
    const [selectedBusinessName, setSelectedBusinessName] = useState<BusinessName | null>(null);
    const [alertBoxDismissed, setAlertBoxDismissed] = useState(false);
    const isInitialLoad = isLoading && businessNames.length === 0;
    const isBusinessNamesAvailable = businessNames.length > 0;
    const canShowAlert = !alertBoxDismissed && isBusinessNamesAvailable;
    const track = useTrack();
    const pathName = useUserContext().forwardedPath || '';
    const currentPagePath = removePathQueryParams(pathName);

    function trackModalOpened() {
        track('Navigation Clicked', {
            pageSection: trackingEventDetails.pageSection,
            category: trackingEventDetails.category,
            label: 'Content:GeneratedNamesGrid',
            eventDetail: `${currentPagePath};${currentPagePath};GeneratedNamesGrid;businessNameButton`,
            navigationDetail: 'businessNameButton',
        });
    }

    return (
        <>
            {selectedBusinessName && (
                <BusinessNameModal
                    businessName={selectedBusinessName}
                    onDismiss={() => setSelectedBusinessName(null)}
                    trackingEventDetails={trackingEventDetails}
                    keyword={keyword}
                    industryKeywords={industryKeywords}
                />
            )}
            <GridContainer>
                {canShowAlert && (
                    <Row style={{ justifyContent: 'center' }}>
                        <Column span={12} marginBottom="between-subsections">
                            <AlertBox
                                skin="info"
                                dismissed={alertBoxDismissed}
                                onRequestDismiss={() => {
                                    setAlertBoxDismissed(true);
                                }}
                                accessibleTextForIcon="info"
                            >
                                <Typography>
                                    Click on a name to learn more about its meaning and check domain
                                    availability.
                                </Typography>
                                <AlertBoxDismissButton accessibleText="Dismiss alert" />
                            </AlertBox>
                        </Column>
                    </Row>
                )}
                <Row as="ul">
                    {isBusinessNamesAvailable &&
                        businessNames.map((currentBusinessName, index) => (
                            <Column
                                key={index}
                                span={3}
                                spanMd={6}
                                spanSm={6}
                                marginBottom={{ xs: '4', sm: '6' }}
                                as="li"
                            >
                                <Button
                                    skin="unstyled"
                                    width="full-width"
                                    onClick={() => {
                                        trackModalOpened();
                                        setSelectedBusinessName(currentBusinessName);
                                    }}
                                >
                                    <Card bordered skin="link">
                                        <FlexBox
                                            justifyContent="space-between"
                                            alignItems="center"
                                            flexDirection="row"
                                            gap="6"
                                        >
                                            <Typography fontSkin="title-item">
                                                {currentBusinessName.name}
                                            </Typography>
                                            <Icon size="20p" iconType="arrowRight" />
                                        </FlexBox>
                                    </Card>
                                </Button>
                            </Column>
                        ))}
                    {isInitialLoad &&
                        getSkeletonBusinessNames().map((currentBusinessName, index) => (
                            <Column
                                key={`${currentBusinessName.name + index}`}
                                span={3}
                                spanMd={6}
                                spanSm={6}
                                marginBottom={{ xs: '4', sm: '6' }}
                            >
                                <Card bordered>
                                    <SkeletonText>
                                        <SkeletonTextHeading />
                                    </SkeletonText>
                                </Card>
                            </Column>
                        ))}
                </Row>
            </GridContainer>
        </>
    );
};

export default BusinessNamesGrid;
